import React, { useCallback, useState, useEffect } from 'react';
import { styled, alpha } from '@mui/material/styles';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { ComputedRoute } from 'utils/ComputedRoute';
import { DRAWER_WIDTH } from 'constants/sizing';
import { useQueryString } from 'hooks/useQueryString';
import { DomId } from 'constants/DomId';
import ItemSearchModal from 'ui/modals/ItemSearchModal';

import messages from './messages';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    marginLeft: `${DRAWER_WIDTH}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const RightHandSide = styled('div')(({ theme }) => ({
  position: 'relative',
  marginLeft: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  top: '0px',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '30ch',
      '&:focus': {
        width: '35ch',
      },
    },
  },
}));

interface IProps {
  open?: boolean;
  onChangeOpen?: () => void;
}

const ApplicationHeader = ({
  open = false,
  onChangeOpen = () => null,
}: IProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { parsedQueryString } = useQueryString();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState<string>(
    searchParams.get('q') || ''
  );
  const [isItemSearchOpen, setIsItemSearchOpen] = useState(false);

  const handleSearch = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Enter') {
        navigate(
          ComputedRoute.getSearchRoute({
            ...parsedQueryString,
            q: searchValue,
            page: 0,
          })
        );
      }
    },
    [navigate, parsedQueryString, searchValue]
  );

  useEffect(() => {
    setSearchValue(searchParams.get('q') || '');
  }, [searchParams]);

  const toggleIsItemSearchOpen = useCallback(() => {
    setIsItemSearchOpen((prev) => !prev);
  }, []);

  return (
    <>
      <AppBar open={open} position='sticky' id={DomId.ApplicationHeader}>
        <Toolbar>
          {!open && (
            <IconButton
              size='large'
              edge='start'
              color='inherit'
              aria-label='open drawer'
              sx={{ mr: 2 }}
              onClick={onChangeOpen}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant='h6'
            noWrap
            component='div'
            sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}
          >
            <FormattedMessage {...messages.appHeader} />
          </Typography>
          <RightHandSide>
            <Tooltip title={formatMessage(messages.helpTooltip)}>
              <IconButton
                component={RouterLink}
                to={ComputedRoute.getHelpRoute()}
                size='large'
                edge='start'
                color='inherit'
                aria-label='open help'
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={formatMessage(messages.itemSearchTooltip)}>
              <IconButton
                size='large'
                edge='start'
                color='inherit'
                aria-label='open item search'
                onClick={toggleIsItemSearchOpen}
              >
                <ListAltIcon />
              </IconButton>
            </Tooltip>

            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value || '')}
                onKeyUp={handleSearch}
                placeholder={formatMessage(messages.searchPlaceholder)}
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search>
          </RightHandSide>
        </Toolbar>
      </AppBar>
      {isItemSearchOpen ? (
        <ItemSearchModal onClose={toggleIsItemSearchOpen} />
      ) : null}
    </>
  );
};

export default ApplicationHeader;
